import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { DrawerContext, ModalContext, Text } from '@notino/react-styleguide';
import {
  AvailabilityState,
  LoyaltyStateEnum,
} from '@notino/shared/definitions/types';

import { LOGIN_URL } from '@constants';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { useUser } from '@queries/useUser';
import { useIsDesktop } from '@utils/helpers';

import { Active } from './components/Descriptions/Active';
import { Inactive } from './components/Descriptions/Inactive';
import { DrawerOrModal } from './components/DrawerOrModal';
import { contentStyles } from './components/DrawerOrModal/Content/styled';
import { Footer } from './components/DrawerOrModal/Footer';
import { hideDefaultHeader } from './components/DrawerOrModal/Header/styled';
import { useLoyaltyStateQuery } from './hooks/useLoyaltyStateQuery';
import { LoyaltySymbol } from './LoyaltySymbol';
import { loyaltyMessages } from './messages';
import {
  CtaLink,
  DescriptionWrapper,
  LoyaltyProgramBannerWrapper,
} from './styled';
import { trackCloseDrawerOrModal, trackLoyalty } from './tracking';

type Props = {
  productQuantity: number;
};

export const LoyaltyProgramBanner = ({ productQuantity }: Props) => {
  const { currentVariant } = useProductDetailContext();
  const { isLoggedIn } = useUser();
  const { getTimeFromInit } = useTrackingContext();

  const isDesktop = useIsDesktop();

  const points = currentVariant.attributes.LoyaltyPoints;

  const hasLoyaltyDrawer =
    points &&
    currentVariant.availability.state === AvailabilityState.CanBeBought;

  const { data } = useLoyaltyStateQuery({ shouldSkip: !hasLoyaltyDrawer });

  const { openDrawer, closeDrawer } = DrawerContext.useDrawerContext();
  const { toggleModal, hideModal } = ModalContext.useModalContext();

  if (!hasLoyaltyDrawer) {
    return null;
  }

  const handleOpenDrawerOrModal = () => {
    trackLoyalty({
      timing: getTimeFromInit(),
      name: 'loyalty_logged_in',
    });

    if (isDesktop) {
      toggleModal(<DrawerOrModal footerContent={<Footer />} />, {
        disableCloseButton: true,
        noBorders: true,
        onClose: () => {
          trackCloseDrawerOrModal({ isOutsideClick: true });
          hideModal();
        },
      });
      return;
    }

    openDrawer(<DrawerOrModal />, {
      footerContent: <Footer />,
      initialPosition: 'dynamic',
      isTitleBorderHidden: true,
      hideScrollbar: true,
      showCloseIcon: false,
      headerCustomStyles: hideDefaultHeader,
      drawerContainerCustomStyles: contentStyles,
      onClose: () => {
        trackCloseDrawerOrModal({ isOutsideClick: true });
        closeDrawer();
      },
    });
  };

  const getComponent = () => {
    if (!isLoggedIn) {
      return (
        <DescriptionWrapper>
          <Text typography="labelSmall400" color="text.secondary">
            <FormattedMessage {...loyaltyMessages.loginText} />
          </Text>

          <CtaLink
            href={LOGIN_URL(currentVariant.url ?? '')}
            onClick={() =>
              trackLoyalty({
                timing: getTimeFromInit(),
                name: 'loyalty_anonymous',
              })
            }
          >
            <FormattedMessage {...loyaltyMessages.login} />
          </CtaLink>
        </DescriptionWrapper>
      );
    }

    if (!data?.getLoyaltyState?.state) {
      return null;
    }

    const { state } = data.getLoyaltyState;

    if (state === LoyaltyStateEnum.Inactive) {
      return <Inactive handleOpenDrawerOrModal={handleOpenDrawerOrModal} />;
    }

    return <Active points={points} productQuantity={productQuantity} />;
  };

  const descriptionComponent = getComponent();

  if (!descriptionComponent) {
    return null;
  }

  return (
    <LoyaltyProgramBannerWrapper>
      <LoyaltySymbol />

      {descriptionComponent}
    </LoyaltyProgramBannerWrapper>
  );
};
